import React, { MouseEvent } from 'react';
import { Menu } from 'antd';
import {
    AddToCustomLayerEvent,
    NewCustomLayerEvent,
    OpenTvMonitoredEvents,
    RemoveFromCustomLayerEvent,
    ShowModalEvent,
} from 'app/common/eventbus/Events';
import AppContext from 'app/common/context/AppContext';
import { CustomLayerUtils, MapLayer, MapLayerUtils, MapObjectType } from 'app/components/dashboard/network/map/Map';
import Utils from 'app/common/util/Utils';
import { Flex } from 'app/components/common/Flex';
import { EntryLoadBar } from 'app/components/dashboard/network/flows/svg/EntryLoadBar';
import { OccupancyLoadBar } from 'app/components/dashboard/network/flows/svg/OccupancyLoadBar';
import { ObjectTypeContextMenu } from 'app/components/dashboard/network/flows/menu/MapContextMenu';
import { MapSelectionValues, NewLayerPopup } from 'app/components/dashboard/network/map/MapSelectionPopup';
import MeasureUtils from 'app/common/util/MeasureUtils';
import { MonitoredEventType, MonitoredEventUtils } from 'app/common/util/MonitoredEventUtils';
import AuthorisationUtils from 'app/common/context/AuthorisationUtils';
import { HotspotLoadProps } from 'app/components/dashboard/network/flows/HotspotLoad';
import { HotspotBar } from 'app/components/dashboard/network/flows/svg/HotspotBar';
import { HotspotPanel } from 'app/components/panels/measure/HotspotPanel';

interface MapHotspotContextMenuProps extends ObjectTypeContextMenu {
    hotspots: Array<HotspotLoadProps>;
    onClose(): void;
}

const ACTION_OPEN_EDIT = 'open-edit';
const ACTION_CREATE_CUSTOM_LAYER = 'create-custom-layer';
const ACTION_ADD_NOTE = 'add-note';
const ACTION_ADD_EVENT = 'add-event';

export class MapHotspotContextMenu extends React.PureComponent<MapHotspotContextMenuProps> {
    render() {
        return <Menu key="map-measure-context-menu" items={this.getMenuItems()} triggerSubMenuAction="click" />;
    }

    getMenuItems = () => {
        const subMenu = this.props.hotspots?.map(hotspotLoadProps => {
            const notes = hotspotLoadProps.monitoredEvents?.filter(MonitoredEventUtils.isNoteEvent);
            const events = hotspotLoadProps.monitoredEvents?.filter(MonitoredEventUtils.isWeatherEvent);
            const tvId = hotspotLoadProps.tv;
            let hotspotMenuItems = undefined;
            if (!AuthorisationUtils.isFmpMapUser()) {
                const measureIds = hotspotLoadProps.measures
                    .filter(m => MeasureUtils.getTvLocation(m.measure).id === tvId)
                    .map(m => MeasureUtils.getTvLocation(m.measure).id);
                const refLoc = hotspotLoadProps.hotspots?.[0].location;
                hotspotMenuItems = [
                    {
                        label: tvId,
                        key: `hotspotload-${tvId}`,
                        className: 'menuItemLoadGraph',
                        disabled: true, //otherwise when clicking it hides the menu
                    },
                    { key: 'divider-1', type: 'divider' },
                    {
                        key: this.actionKey(ACTION_OPEN_EDIT, tvId),
                        label: `Open/edit ${hotspotLoadProps.tv}`,
                        onClick: () =>
                            ShowModalEvent.fireNotClosable(
                                <HotspotPanel tv={tvId} />,
                                HotspotPanel.HOTSPOT_PANEL_WIDTH_EM,
                            ),
                    },
                    { key: 'divider-4', type: 'divider' },
                    {
                        label: 'Create a custom layer',
                        key: this.actionKey(ACTION_CREATE_CUSTOM_LAYER, tvId),
                        onClick: () => this.showCustomLayerNamePopup(tvId),
                    },
                    this.addToCustomLayerMenu(hotspotLoadProps),
                    ...this.removeFromCustomLayerMenu(tvId),
                    { key: 'divider-5', type: 'divider' },
                    {
                        key: this.actionKey(ACTION_ADD_NOTE, tvId),
                        label: `${Utils.isNotEmpty(notes) ? 'Show notes' : 'Add a note'}`,
                        onClick: () =>
                            OpenTvMonitoredEvents.fire(MonitoredEventType.NOTE, tvId, refLoc, undefined, measureIds),
                    },
                    {
                        key: this.actionKey(ACTION_ADD_EVENT, tvId),
                        label: `${Utils.isNotEmpty(events) ? 'Show events' : 'Add an event'}`,
                        onClick: () =>
                            OpenTvMonitoredEvents.fire(
                                MonitoredEventType.WEATHER_EVENT,
                                tvId,
                                refLoc,
                                undefined,
                                measureIds,
                            ),
                    },
                ];
            }
            return {
                label: this.getHotspotNameLabel(hotspotLoadProps),
                key: tvId,
                children: hotspotMenuItems,
            };
        });
        if (this.props.group) {
            return [
                {
                    label: <span className="detailed-subpanel-title">Hotspot ({this.props.hotspots.length})</span>,
                    key: 'measures',
                    children: subMenu,
                },
            ];
        } else {
            return subMenu;
        }
    };

    getTvCustomLayer(tvId: string) {
        return CustomLayerUtils.customLayers.filter(
            customLayer =>
                customLayer.objectsToDisplay.filter(
                    objectToDisplay =>
                        MapObjectType.HOTSPOT === objectToDisplay.type &&
                        tvId === MapLayerUtils.HOTSPOT_TV_ID_PROVIDER.getKey(objectToDisplay.object),
                )?.length > 0,
        );
    }

    getHotspotNameLabel(hotspotLoadProps: HotspotLoadProps) {
        const monitoredPeriod = AppContext.getMonitoringPeriod();
        return (
            <div>
                <Flex grow={false} onClick={event => this.handleHotspotLoadClick(event, hotspotLoadProps.tv)}>
                    <Flex size="6.5em">
                        ID: <span style={{ color: 'white' }}>{hotspotLoadProps.tv}</span>
                    </Flex>
                    <Flex direction="vertical" style={{ alignSelf: 'center', paddingLeft: '1em' }}>
                        {hotspotLoadProps.monitoredTv?.loadValues && (
                            <EntryLoadBar
                                key="entry-load"
                                loadValues={hotspotLoadProps.monitoredTv.loadValues}
                                monitoredPeriod={monitoredPeriod}
                                activePeriods={hotspotLoadProps.monitoredTv.activePeriods}
                                currentTime={hotspotLoadProps.currentTime}
                                drawBottomLine={true}
                                barHeightEm={0.5}
                            />
                        )}
                        {hotspotLoadProps.monitoredTv?.occupancyLoadValues &&
                            hotspotLoadProps.monitoredTv?.occupancyLoadValues.map(occupancyValues => {
                                return (
                                    <OccupancyLoadBar
                                        key={'occupancy-load-' + occupancyValues.durationInMin}
                                        loadValues={occupancyValues.occupancyLoadValues}
                                        monitoredPeriod={monitoredPeriod}
                                        activePeriods={hotspotLoadProps.monitoredTv.activePeriods}
                                        currentTime={hotspotLoadProps.currentTime}
                                        drawBottomLine={true}
                                        barHeightEm={0.5}
                                    />
                                );
                            })}
                        {hotspotLoadProps.hotspots.map((hotspot, idx) => (
                            <HotspotBar
                                key={`hotspot-bar-${idx}`}
                                barHeight={1}
                                hotspot={hotspot}
                                monitoredPeriod={monitoredPeriod}
                            />
                        ))}
                    </Flex>
                </Flex>
            </div>
        );
    }

    private handleHotspotLoadClick = (event: MouseEvent<HTMLDivElement>, tv: string) => {
        event.stopPropagation();
        this.props.onClose();
        ShowModalEvent.fireNotClosable(<HotspotPanel tv={tv} />, HotspotPanel.HOTSPOT_PANEL_WIDTH_EM);
    };

    addToCustomLayerMenu(hotspotLoadProps: HotspotLoadProps) {
        const tvId = hotspotLoadProps.tv;
        const result = {
            label: 'Add to custom layer',
            key: 'add-to-custom-layer-menu-' + tvId,
            children: [
                ...CustomLayerUtils.customLayers
                    .filter(
                        customLayer =>
                            !customLayer.objectsToDisplay.some(
                                objectToDisplay =>
                                    MapObjectType.HOTSPOT === objectToDisplay.type &&
                                    tvId === MapLayerUtils.HOTSPOT_TV_ID_PROVIDER.getKey(objectToDisplay.object),
                            ),
                    )
                    .map(customLayer => ({
                        key: 'add-to-layer-' + customLayer.id + tvId,
                        label: `Add to: ${customLayer.label}`,
                        onClick: () => this.addToCustomLayerMenuHandleUpdateSection(tvId, customLayer),
                    })),
            ],
        };

        if (result.children.length > 0) {
            return result;
        }
    }

    removeFromCustomLayerMenu(tvId: string) {
        const tvCustomLayers = this.getTvCustomLayer(tvId);
        if (tvCustomLayers?.length > 0) {
            return [
                { key: 'divider-cl' + tvId, type: 'divider' },
                {
                    key: 'remove-from-layer-' + tvId,
                    label: 'Remove from custom layer',
                    children: [
                        ...tvCustomLayers.map(customLayer => ({
                            key: 'remove-from-layer-' + customLayer.id + tvId,
                            label: `Remove from: ${customLayer.label}`,
                            onClick: () => this.removeFromCustomLayerMenuHandleUpdateSection(tvId, customLayer),
                        })),
                    ],
                },
            ];
        }
        return [];
    }

    removeFromCustomLayerMenuHandleUpdateSection(tvId: string, customLayer: MapLayer) {
        this.props.onClose();
        RemoveFromCustomLayerEvent.fire(customLayer, tvId, MapObjectType.HOTSPOT);
    }

    actionKey(action: string, measureId: string) {
        return `${action}_${measureId}`;
    }

    addToCustomLayerMenuHandleUpdateSection(tvId: string, customLayer: MapLayer) {
        this.props.onClose();
        AddToCustomLayerEvent.fire(customLayer, tvId, MapObjectType.HOTSPOT);
    }

    showCustomLayerNamePopup(tvName: string) {
        const value: MapSelectionValues = Object.assign(
            {},
            {
                [MapLayerUtils.TV_MAP_SELECTION_POPUP_FIELD]: {
                    label: 'TFV',
                    options: [tvName],
                    selected: [tvName],
                },
            },
        );
        const content = <NewLayerPopup onSave={name => this.handleSaveNewLayer(value, name)} />;
        AppContext.eventBus.publish(new ShowModalEvent(ShowModalEvent.NAME, content, '200px'));
    }

    handleSaveNewLayer = (value: MapSelectionValues, name: string) => {
        const values: Map<MapObjectType, Array<string>> = new Map<MapObjectType, Array<string>>();
        values.set(MapObjectType.TV, value[MapLayerUtils.TV_MAP_SELECTION_POPUP_FIELD].selected);
        NewCustomLayerEvent.fire(name, values);
    };
}
